import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import stabilityGrid from "../../img/trade/stability-grid.svg";
import securityArrowLeft from "../../img/experience/security-arrow-left.svg";
import securityArrowRight from "../../img/experience/security-arrow-right.svg";

const CARDS = [
  { numKey: "trade.stability.c1.num", titleKey: "trade.stability.c1.title", descKey: "trade.stability.c1.desc", w: "xl:w-[394px]" },
  { numKey: "trade.stability.c2.num", titleKey: "trade.stability.c2.title", descKey: "trade.stability.c2.desc", w: "xl:w-[393px]" },
  { numKey: "trade.stability.c3.num", titleKey: "trade.stability.c3.title", descKey: "trade.stability.c3.desc", w: "xl:w-[393px]" },
  { numKey: "trade.stability.c4.num", titleKey: "trade.stability.c4.title", descKey: "trade.stability.c4.desc", w: "xl:w-[394px]" },
];

function StabilityCard({ card }) {
  const { t } = useTranslation();
  return (
    <div className={`bg-[#ee5414] rounded-[20px] p-[30px] xl:p-[40px] flex flex-col h-[266px] xl:h-[315px] xl:shrink-0 ${card.w}`}>
      <div className="flex flex-col xl:h-[235px] xl:justify-between h-full justify-between">
        <span className="font-['Montserrat'] font-bold text-[13px] tracking-[1.3px] text-[#ffb393]">
          {t(card.numKey)}
        </span>
        <div className="flex flex-col gap-[12px]">
          <h3 className="font-['Montserrat'] font-extrabold text-[20px] xl:text-[24px] text-white leading-normal">
            {t(card.titleKey)}
          </h3>
          <p className="font-['Inter'] font-normal text-[14px] xl:text-[16px] text-[#ffb393] leading-[1.4]">
            {t(card.descKey)}
          </p>
        </div>
      </div>
    </div>
  );
}

function StabilityCardMobile({ card }) {
  const { t } = useTranslation();
  return (
    <div className="bg-[#ee5414] rounded-[14px] px-[22px] pt-[22px] pb-[26px] flex flex-col justify-between h-[195px]">
      <span className="font-['Montserrat'] font-bold text-[11px] tracking-[1.1px] text-[#ffb393]">
        {t(card.numKey)}
      </span>
      <div className="flex flex-col gap-[10px]">
        <h3 className="font-['Montserrat'] font-extrabold text-[16px] text-white leading-normal">
          {t(card.titleKey)}
        </h3>
        <p className="font-['Inter'] font-normal text-[12px] text-[#ffb393] leading-[1.35]">
          {t(card.descKey)}
        </p>
      </div>
    </div>
  );
}

function Stability() {
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="mt bg-[#0a0a0a] relative overflow-hidden py-[60px] xl:py-[160px]">
      {/* Grid overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <img src={stabilityGrid} alt="" aria-hidden className="absolute inset-0 w-full h-full" />
      </div>

      <div className="relative z-[1]">

        {/* Desktop layout */}
        <div className="glcontainer hidden xl:flex flex-col gap-[20px]">

          {/* Row 1: title (393px) + card1 (394px) + card2 (393px) */}
          <div className="flex gap-[20px] items-start">
            <div className="w-[393px] shrink-0 flex items-center">
              <h2 className="font-['Montserrat'] font-black text-[46px] text-white tracking-[-0.92px] uppercase leading-none">
                {t("trade.stability.title")}
              </h2>
            </div>
            <div className="flex gap-[20px]">
              <StabilityCard card={CARDS[0]} />
              <StabilityCard card={CARDS[1]} />
            </div>
          </div>

          {/* Row 2: card3 (393px) + card4 (394px) → gap-20 within, gap-40 to desc (373px) */}
          <div className="flex gap-[40px] items-center">
            <div className="flex gap-[20px] shrink-0">
              <StabilityCard card={CARDS[2]} />
              <StabilityCard card={CARDS[3]} />
            </div>
            <p className="font-['Inter'] font-normal text-[18px] text-[#717171] leading-[1.4] w-[373px] shrink-0">
              {t("trade.stability.desc")}
            </p>
          </div>

        </div>

        {/* Mobile layout */}
        <div className="xl:hidden flex flex-col gap-[16px] min-w-0">
          <div className="glcontainer flex flex-col gap-[14px] min-w-0">
            <h2 className="font-['Montserrat'] font-black text-[24px] text-white tracking-[-0.92px] uppercase leading-none">
              {t("trade.stability.title")}
            </h2>

            <Swiper
              slidesPerView={1}
              spaceBetween={12}
              onSwiper={(swiper) => { swiperRef.current = swiper; }}
              onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
              className="w-full min-w-0 overflow-hidden"
            >
              {CARDS.map((card, i) => (
                <SwiperSlide key={i} className="!h-auto">
                  <StabilityCardMobile card={card} />
                </SwiperSlide>
              ))}
            </Swiper>

            <div className="bg-[#1e1e1e] h-[20px] rounded-[20px] px-[14px] flex items-center justify-between">
              <button
                type="button"
                onClick={() => swiperRef.current?.slidePrev()}
                className="flex items-center justify-center size-[14px] opacity-70"
                aria-label="Previous"
              >
                <img src={securityArrowLeft} alt="" aria-hidden className="size-[10px]" style={{ transform: "rotate(-135deg)" }} />
              </button>

              <div className="flex items-center gap-[6px]">
                {CARDS.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => swiperRef.current?.slideTo(i)}
                    className={`rounded-full transition-all duration-200 ${
                      i === activeIndex
                        ? "bg-[#ee5414] w-[6px] h-[6px]"
                        : "bg-[#444] w-[6px] h-[6px]"
                    }`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => swiperRef.current?.slideNext()}
                className="flex items-center justify-center size-[14px] opacity-70"
                aria-label="Next"
              >
                <img src={securityArrowRight} alt="" aria-hidden className="size-[10px]" style={{ transform: "rotate(45deg)" }} />
              </button>
            </div>

            <p className="font-['Inter'] font-normal text-[12px] text-[#717171] leading-[1.35]">
              {t("trade.stability.desc")}
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}

export default Stability;
